import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import MobileBottomNav from '../components/MobileBottomNav';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const { t } = useLanguage();
  const { theme } = useTheme();
  const currentLogo = theme === 'light' 
    ? "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png"
    : "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('forgotPasswordError') || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-page">
        <Helmet>
          <title>Forgot Password - Registan Tourism</title>
          <link rel="canonical" href="https://registantours.com/forgot-password" />
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <div className="auth-container">
          <div className="auth-header">
            <div className="success-icon">
              <CheckCircle size={64} />
            </div>
            <h1>{t('checkYourEmail') || 'Check Your Email'}</h1>
            <p>{t('resetEmailSent') || `We've sent a password reset link to ${email}. Please check your inbox.`}</p>
          </div>
          
          <div className="auth-footer" style={{ marginTop: '32px' }}>
            <Link to="/login" className="btn-primary btn-auth" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
              {t('backToLogin') || 'Back to Login'}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <button className="back-btn" onClick={() => navigate('/login')}>
          <ArrowLeft size={20} />
          {t('back') || 'Back'}
        </button>
        
        <div className="auth-header">
          <img src={currentLogo} alt="Registan" className="auth-logo-img" />
          <h1>{t('forgotPasswordTitle') || 'Forgot Password?'}</h1>
          <p>{t('forgotPasswordDesc') || "Enter your email address and we'll send you a link to reset your password."}</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="auth-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">{t('emailAddress') || 'Email Address'}</label>
            <div className="input-with-icon">
              <Mail size={20} />
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                data-testid="forgot-email-input"
              />
            </div>
          </div>

          <button 
            type="submit" 
            className="btn-primary btn-auth"
            disabled={loading}
            data-testid="forgot-submit-btn"
          >
            {loading ? (t('sending') || 'Sending...') : (t('sendResetLink') || 'Send Reset Link')}
          </button>
        </form>

        <div className="auth-footer">
          <p>{t('rememberPassword') || 'Remember your password?'} <Link to="/login">{t('signIn') || 'Sign In'}</Link></p>
          <p style={{ marginTop: '12px', fontSize: '12px', opacity: 0.7 }}>
            <Link to="/privacy" style={{ color: 'inherit' }} data-testid="auth-privacy-link">{t('privacyPolicy') || 'Privacy Policy'}</Link>
          </p>
        </div>
      </div>
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
};

export default ForgotPasswordPage;
